import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Box, Input, Spinner, Stack, Switch, Text, chakra } from '@chakra-ui/react';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { ChromeDialog, useDialogScreen } from '../ui/chrome_dialog.tsx';
import { IconGlyph } from '../ui/icon_glyph.tsx';

// The rows of a form, as iOS draws them (the owner, 2026-10-16): one line each, with the label and
// what is set. `ScreenRow` opens a screen of its own, in the dialog's place (a stack, to any depth)
// or as a dialog outside one: the options of a choice, a sub-form, a list (spec §5.6).
// `ChoiceField` takes one value from a list: each option with its detail (a device's kind and
// room, an attribute's value; spec T-11), a filter when the list is long, sections ("Fits this
// tile", "Other"), and rows of their own at the end ("New connection…"). Not the platform's
// select: that shows no details, and it does not suit long lists.
//
// The rows belong in a `SettingsGroup`, beside the other fields of their form.

const FieldRowButton = chakra('button', {
    base: {
        display: 'flex',
        alignItems: 'center',
        gap: '3',
        width: '100%',
        minH: '12',
        px: '4',
        py: '3',
        textAlign: 'start',
        cursor: 'pointer',
        _hover: { bg: 'bg.muted' },
        _disabled: { cursor: 'default', _hover: { bg: 'transparent' } },
    },
});

/** What the row says on the right, before its chevron. */
function RowValue({ children, color = 'fg.muted' }: { children: ReactNode; color?: string }) : ReactNode {
    return (
        <Box flex="1" minW="0" display="flex" justifyContent="flex-end" color={color}>
            {typeof children === 'string' ? <Text truncate>{children}</Text> : children}
        </Box>
    );
}

export interface ScreenRowProps {
    label: string;
    /** What is set, on the right. */
    value: ReactNode;
    /** The screen's title; the label when left out. */
    screenTitle?: string;
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    /** The screen's content, drawn while it is open. */
    screen: ReactNode;
    isDisabled?: boolean;
    valueColor?: string | undefined;
}

/** A row that opens a screen of its own: in the dialog's stack, or as a dialog outside one. */
export function ScreenRow(props: ScreenRowProps) : ReactNode {
    const { label, value, screenTitle = label, isOpen, onOpen, onClose, screen, isDisabled = false, valueColor } = props;
    const { isInDialog, slot } = useDialogScreen(isOpen, screenTitle, onClose);

    let opened: ReactNode = null;
    if (isOpen) {
        if (isInDialog) {
            opened = slot ? createPortal(screen, slot) : null;
        } else {
            opened = (
                <ChromeDialog title={screenTitle} onClose={onClose} focusesField={false}>
                    {screen}
                </ChromeDialog>
            );
        }
    }

    return (
        <>
            <FieldRowButton type="button" aria-label={label} data-choice-field={label} disabled={isDisabled} onClick={onOpen}>
                <Text fontWeight="medium" flex="none" color={isDisabled ? 'fg.muted' : undefined}>
                    {label}
                </Text>
                <RowValue {...(valueColor ? { color: valueColor } : {})}>{value}</RowValue>
                {isDisabled ? null : <IconGlyph name="chevron-right" size={14} color="#71717a" />}
            </FieldRowButton>
            {opened}
        </>
    );
}

/** A field that waits for others (spec F-4): greyed, saying what it waits for. */
export function WaitingRow({ label, waitingFor }: { label: string; waitingFor: readonly string[] }) : ReactNode {
    return (
        <Box display="flex" alignItems="center" gap="3" minH="12" px="4" py="3" data-choice-field={label} aria-disabled>
            <Text fontWeight="medium" flex="none" color="fg.muted">
                {label}
            </Text>
            <RowValue>{`Choose ${waitingFor.map((title) => title.toLowerCase()).join(' and ')} first`}</RowValue>
        </Box>
    );
}

export interface ChoiceOption {
    readonly value: string;
    readonly label: string;
    readonly detail?: string | undefined;
    /** Choices with a section are shown under its title, in the order the sections first appear. */
    readonly section?: string | undefined;
}

/** A row after the choices that does something else than choose. */
export interface ChoiceExtra {
    readonly label: string;
    readonly onSelect: () => void;
    /** The screen stays open after it, for a row that changes the list. */
    readonly keepsOpen?: boolean;
}

export interface ChoiceFieldProps {
    label: string;
    options: readonly ChoiceOption[];
    value: string | undefined;
    onChange: (value: string | undefined) => void;
    /** What the field says before anything is chosen. */
    placeholder?: string;
    /** What the field is for, at the top of its screen. */
    helper?: ReactNode;
    /** Adds "None" at the top, which clears the value. */
    isOptional?: boolean;
    /** What that row says where clearing means something else than none: "Default (Right)". */
    noneLabel?: string;
    extras?: readonly ChoiceExtra[];
    /** Asking for the choices: the field waits, with this text. */
    waiting?: string | undefined;
    /** Asking failed: the field says so, and a tap asks again. */
    failed?: { reason: string; onRetry: () => void } | undefined;
    /** Lists longer than this get a filter. */
    filterFrom?: number;
    /** Said in orange in place of the placeholder while nothing is chosen: what is wrong with leaving it so. */
    warning?: string | undefined;
}

/** The options by section, in the order the sections first appear. */
function sectionsOf(options: readonly ChoiceOption[]) : [string | undefined, ChoiceOption[]][] {
    const sections = new Map<string | undefined, ChoiceOption[]>();
    for (const option of options) {
        sections.set(option.section, [...(sections.get(option.section) ?? []), option]);
    }

    return [...sections];
}

export function ChoiceField(props: ChoiceFieldProps) : ReactNode {
    const { label, options, value, onChange, placeholder = 'Choose…', helper, isOptional = false, noneLabel = 'None', extras = [], waiting, failed, filterFrom = 12, warning } = props;
    const [isOpen, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const chosen = options.find((option) => option.value === value);

    const close = () : void => {
        setOpen(false);
        setFilter('');
    };
    const choose = (next: string | undefined) : void => {
        onChange(next);
        close();
    };

    if (failed) {
        return (
            <FieldRowButton type="button" aria-label={label} data-choice-field={label} onClick={failed.onRetry}>
                <Text fontWeight="medium" flex="none">
                    {label}
                </Text>
                <RowValue color="#f87171">{`Failed: ${failed.reason}. Tap to try again`}</RowValue>
            </FieldRowButton>
        );
    }

    // A value the list does not have (yet) is shown as it is.
    const face = waiting !== undefined ? (
        <Box display="inline-flex" alignItems="center" gap="2">
            <Spinner size="xs" />
            <Text truncate>{waiting}</Text>
        </Box>
    ) : chosen === undefined && value === undefined && warning !== undefined ? (
        <Text as="span" color="orange.300">
            {warning}
        </Text>
    ) : (chosen?.label ?? value ?? placeholder);

    const needle = filter.trim().toLowerCase();
    const shown = needle === '' ? options : options.filter((option) => `${option.label} ${option.detail ?? ''}`.toLowerCase().includes(needle));
    const screen = (
        <Stack gap="4" role="listbox" aria-label={label}>
            {helper ? (
                <Text fontSize="sm" color="fg.muted" px="1">
                    {helper}
                </Text>
            ) : null}
            {options.length > filterFrom ? (
                <Input aria-label={`Filter ${label.toLowerCase()}`} placeholder={`Filter ${options.length}…`} value={filter} onChange={(event) => setFilter(event.target.value)} />
            ) : null}
            {isOptional && needle === '' ? (
                <SettingsGroup>
                    <SettingsRow title={<Text as="span" color="fg.muted">{noneLabel}</Text>} isCurrent={value === undefined} opens={false} onClick={() => choose(undefined)} />
                </SettingsGroup>
            ) : null}
            {sectionsOf(shown).map(([section, sectionOptions]) => (
                <SettingsGroup key={section ?? ''} title={section}>
                    {sectionOptions.map((option) => (
                        <SettingsRow key={option.value} title={option.label} detail={option.detail} isCurrent={option.value === value} opens={false} onClick={() => choose(option.value)} />
                    ))}
                </SettingsGroup>
            ))}
            {shown.length === 0 || extras.length > 0 ? (
                <SettingsGroup>
                    {shown.length === 0 ? <SettingsRow title={<Text as="span" color="fg.muted">{options.length === 0 ? 'Nothing to choose from' : 'Nothing matches'}</Text>} /> : null}
                    {extras.map((extra) => (
                        <SettingsRow
                            key={extra.label}
                            title={<Text as="span" color="blue.400">{extra.label}</Text>}
                            opens={false}
                            onClick={() => {
                                extra.onSelect();
                                if (!extra.keepsOpen) {
                                    close();
                                }
                            }}
                        />
                    ))}
                </SettingsGroup>
            ) : null}
        </Stack>
    );

    return <ScreenRow label={label} value={face} isOpen={isOpen} onOpen={() => setOpen(true)} onClose={close} screen={screen} isDisabled={waiting !== undefined} />;
}

/**
 * A typed field beside its label (and what it is for, under the label), used on Enter or when it
 * loses the focus; with a swatch of the colour typed, for a colour.
 */
export function TextFieldRow(props: { label: string; detail?: string | undefined; value: string | undefined; type?: 'text' | 'number' | 'password'; placeholder?: string | undefined; swatch?: string | undefined; onChange: (value: string | undefined) => void }) : ReactNode {
    const { label, detail, value, type = 'text', placeholder, swatch, onChange } = props;
    const [typed, setTyped] = useState(value ?? '');
    const use = () : void => {
        const trimmed = typed.trim();
        if (trimmed !== (value ?? '')) {
            onChange(trimmed === '' ? undefined : trimmed);
        }
    };

    return (
        <Box display="flex" alignItems="center" gap="3" minH="12" px="4" py="1.5">
            <Box flex="1" minW="0">
                <Text fontWeight="medium" truncate>
                    {label}
                </Text>
                {detail ? (
                    <Text fontSize="sm" color="fg.muted" lineClamp={2}>
                        {detail}
                    </Text>
                ) : null}
            </Box>
            {swatch !== undefined ? <Box width="5" height="5" flex="none" borderRadius="sm" borderWidth="1px" borderColor="border" style={{ background: swatch }} data-swatch /> : null}
            <Input
                aria-label={label}
                width="40%"
                maxW="16rem"
                flex="none"
                size="sm"
                variant="flushed"
                textAlign="end"
                type={type}
                placeholder={placeholder}
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                onBlur={use}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        use();
                    }
                }}
            />
        </Box>
    );
}

/** On or off beside its label (and what it is for, under the label). */
export function ToggleRow({ label, detail, checked, onChange }: { label: string; detail?: string | undefined; checked: boolean; onChange: (checked: boolean) => void }) : ReactNode {
    return (
        <Box display="flex" alignItems="center" gap="3" minH="12" px="4" py="1.5">
            <Box flex="1" minW="0">
                <Text fontWeight="medium" truncate>
                    {label}
                </Text>
                {detail ? (
                    <Text fontSize="sm" color="fg.muted" lineClamp={2}>
                        {detail}
                    </Text>
                ) : null}
            </Box>
            <Switch.Root checked={checked} onCheckedChange={(details) => onChange(details.checked)}>
                <Switch.HiddenInput aria-label={label} />
                <Switch.Control />
            </Switch.Root>
        </Box>
    );
}
